import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import {
  AUDIT_LOG_ACTIONS,
  AUDIT_LOG_LOGIN_METHODS,
  AUDIT_LOG_LOGIN_REASONS,
  AUDIT_LOG_LOGIN_TYPE,
  AUDIT_LOG_SOURCES,
  AUDIT_LOG_TABLES,
  AuditLog,
  AuditLogChange,
  AuditLogFilter,
  AuditLogPage,
} from 'src/app/models/AuditLog';
import { User } from 'src/app/models/User';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { AuditLogService } from 'src/app/shared/services/audit-log.service';
import { UserService } from 'src/app/shared/services/user.service';

const ROUTE = '/audit-logs';
const FILTER_KEYS: (keyof AuditLogFilter)[] = [
  'auditable_type', 'auditable_id', 'owner_user_id', 'user_id', 'action', 'source', 'from', 'to',
];

/** Busca de usuário de um campo do filtro: um debounce e uma request por campo. */
interface UserLookup {
  timer: any;
  sub: Subscription | null;
}

/**
 * Consulta da trilha de auditoria dos cadastros (GET /audit-logs e
 * GET /audit-logs/{id}). Somente administrador.
 *
 * Os filtros vivem na query string, então a URL de uma consulta pode ser
 * compartilhada e o voltar do navegador refaz a busca anterior.
 */
@Component({
  selector: 'app-audit-logs',
  templateUrl: './audit-logs.component.html',
  styleUrls: ['./audit-logs.component.scss'],
})
export class AuditLogsComponent implements OnInit, OnDestroy {
  /** Só o valor INTEIRO em ISO-8601 (AuditObserver::normalize) vira data. */
  private static readonly ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;
  /** Quantos nomes de campo o resumo da linha mostra antes do "+N". */
  private static readonly FIELDS_PREVIEW = 6;

  @ViewChild('detailModal') detailModalTpl!: TemplateRef<unknown>;
  @ViewChild('inputOwner') inputOwner: ElementRef<HTMLInputElement>;
  @ViewChild('inputOperator') inputOperator: ElementRef<HTMLInputElement>;

  readonly actions = AUDIT_LOG_ACTIONS;
  readonly crudActions = AUDIT_LOG_ACTIONS.filter((a) => a.group === 'crud');
  readonly loginActions = AUDIT_LOG_ACTIONS.filter((a) => a.group === 'login');
  readonly sources = AUDIT_LOG_SOURCES;
  readonly tables = AUDIT_LOG_TABLES;

  result: AuditLogPage | null = null;
  isLoading = false;
  error: string | null = null;

  page = 1;
  perPage = 15;
  /** Rascunho ligado ao formulário; só vale depois do Filtrar. */
  filter: AuditLogFilter = {};
  /** O que está na URL e foi de fato consultado — base da paginação. */
  applied: AuditLogFilter = {};

  /**
   * Com matAutocomplete o valor do control é a string digitada até um usuário
   * ser escolhido — por isso a leitura passa sempre por selectedUser().
   */
  ownerControl = new FormControl<User | null>(null);
  filteredOwners$: Observable<User[]> | null = null;

  operatorControl = new FormControl<User | null>(null);
  filteredOperators$: Observable<User[]> | null = null;

  detail: AuditLog | null = null;
  detailLoading = false;
  detailError: string | null = null;

  private listSub: Subscription | null = null;
  private detailSub: Subscription | null = null;
  private routeSub: Subscription | null = null;
  private modalRef: NgbModalRef | null = null;
  private ownerLookup: UserLookup = { timer: null, sub: null };
  private operatorLookup: UserLookup = { timer: null, sub: null };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private auditLogService: AuditLogService,
    private userService: UserService,
    private toastService: ToastService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.queryParams.subscribe((params) => {
      this.readParams(params);
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.listSub?.unsubscribe();
    this.detailSub?.unsubscribe();
    [this.ownerLookup, this.operatorLookup].forEach((lookup) => {
      clearTimeout(lookup.timer);
      lookup.sub?.unsubscribe();
    });
    // O modal vive no body: sem isto fica órfão ao sair da tela com ele aberto.
    this.modalRef?.dismiss();
  }

  /** Filtro em vigor (o da URL), não o que está sendo digitado. */
  get hasFilter(): boolean {
    return FILTER_KEYS.some((key) => !!this.applied[key]);
  }

  /** Mostra o botão Limpar também para o que foi digitado e não aplicado. */
  get hasDraft(): boolean {
    return this.hasFilter
      || FILTER_KEYS.some((key) => !!this.filter[key])
      || !!this.ownerControl.value
      || !!this.operatorControl.value;
  }

  /**
   * Início do período padrão quando o backend o aplicou. Quem decide o recorte
   * (e quantos dias) é o backend; a tela só avisa, para o total da paginação
   * não parecer a trilha inteira.
   */
  get defaultPeriodFrom(): string | null {
    return this.result?.applied_from || null;
  }

  load(): void {
    // Antes do `isLoading = true`: o finalize da request anterior dispara no
    // unsubscribe e desligaria o spinner desta.
    this.listSub?.unsubscribe();

    this.isLoading = true;
    this.error = null;

    let received = false;

    this.listSub = this.auditLogService
      .list(this.page, this.perPage, this.applied)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (result) => {
          received = true;
          this.result = result;
        },
        error: (err: HttpErrorResponse) => {
          this.result = null;
          this.error = this.errorMessage(err, 'Não foi possível carregar os logs de auditoria.');
        },
        // Status 0 (rede/CORS): o LoadingInterceptor mostra o toast e devolve
        // of(), então a request completa sem valor e sem erro. Sem isto a tela
        // ficaria com a lista anterior como se fosse o resultado novo.
        complete: () => {
          if (!received) {
            this.result = null;
            this.error = 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
          }
        },
      });
  }

  applyFilter(): void {
    this.filter.owner_user_id = this.selectedUser(this.ownerControl)?.id || null;
    this.filter.user_id = this.selectedUser(this.operatorControl)?.id || null;

    if (this.filter.from && this.filter.to && this.filter.to < this.filter.from) {
      this.toastService.error('A data final não pode ser anterior à inicial.');
      return;
    }

    this.navigate(this.filter, 1);
  }

  clearFilters(): void {
    this.filter = {};
    this.ownerControl.setValue(null);
    this.operatorControl.setValue(null);
    this.navigate(this.filter, 1);
  }

  /** Pagina o filtro aplicado: o que foi digitado e não filtrado fica de fora. */
  pageChanged(page: number): void {
    this.navigate(this.applied, page);
  }

  /** Atalho da linha: histórico completo deste registro. */
  filterByRecord(log: AuditLog): void {
    this.filter = { auditable_type: log.auditable_type, auditable_id: log.auditable_id };
    this.ownerControl.setValue(null);
    this.operatorControl.setValue(null);
    this.navigate(this.filter, 1);
  }

  /** Atalho da linha: tudo o que mudou na conta dona do registro. */
  filterByOwner(log: AuditLog): void {
    if (!log.owner_user_id) {
      return;
    }
    // Parte do filtro aplicado, não do que estava digitado e não filtrado.
    this.syncDraft();
    this.ownerControl.setValue({ id: log.owner_user_id } as User);
    this.applyFilter();
  }

  /** Atalho da linha: tudo o que este operador alterou. */
  filterByOperator(log: AuditLog): void {
    if (!log.user_id) {
      return;
    }
    this.syncDraft();
    this.operatorControl.setValue({ id: log.user_id, name: log.user_name, email: log.user_email } as User);
    this.applyFilter();
  }

  filterOwner(): void {
    this.filterUsers(this.inputOwner, this.ownerControl, this.ownerLookup, (users) => (this.filteredOwners$ = users));
  }

  filterOperator(): void {
    this.filterUsers(
      this.inputOperator, this.operatorControl, this.operatorLookup, (users) => (this.filteredOperators$ = users)
    );
  }

  clearOwner(): void {
    this.ownerControl.patchValue(null);
    this.filteredOwners$ = null;
  }

  clearOperator(): void {
    this.operatorControl.patchValue(null);
    this.filteredOperators$ = null;
  }

  displayUser(user?: User | null): string {
    if (!user || typeof user !== 'object') {
      return '';
    }
    if (user.name && user.email) {
      return `${user.name} (${user.email})`;
    }
    // Vindo da URL/atalho, até o findById responder só temos o id.
    return user.name || user.email || user.id;
  }

  openDetail(log: AuditLog): void {
    this.modalRef?.dismiss();
    this.detailSub?.unsubscribe();

    // Mostra o que a lista já tem enquanto o detalhe chega.
    this.detail = log;
    this.detailError = null;
    this.detailLoading = true;

    this.modalRef = this.modalService.open(this.detailModalTpl, {
      size: 'xl',
      ariaLabelledBy: 'auditLogDetailTitle',
      scrollable: true,
    });

    const ref = this.modalRef;
    ref.result
      .catch(() => undefined)
      .then(() => {
        if (this.modalRef === ref) {
          this.modalRef = null;
          this.detailSub?.unsubscribe();
        }
      });

    this.detailSub = this.auditLogService
      .find(log.id)
      .pipe(finalize(() => (this.detailLoading = false)))
      .subscribe({
        next: (detail) => (this.detail = detail),
        error: (err: HttpErrorResponse) =>
          (this.detailError = err.status === 404
            ? 'Registro de auditoria não encontrado.'
            : 'Não foi possível carregar o detalhe do registro.'),
      });
  }

  actionLabel(action: string): string {
    return this.actions.find((a) => a.value === action)?.label || action;
  }

  actionClass(action: string): string {
    return this.actions.some((a) => a.value === action) ? `action-${action.replace(/_/g, '-')}` : 'action-other';
  }

  isLogin(log: AuditLog): boolean {
    return log.auditable_type === AUDIT_LOG_LOGIN_TYPE;
  }

  /** Valor de um campo de changed_fields (nas linhas de login fica em `new`). */
  loginField(log: AuditLog, field: string): any {
    return (log.changed_fields || []).find((c) => c.field === field)?.new ?? null;
  }

  loginReason(log: AuditLog): string | null {
    const reason = this.loginField(log, 'reason');
    const label = reason ? AUDIT_LOG_LOGIN_REASONS[reason] || reason : null;
    // Gravado só quando esta falha foi a que desativou a conta.
    return label && this.loginField(log, 'account_deactivated') ? `${label} — conta desativada` : label;
  }

  loginMethod(log: AuditLog): string | null {
    const method = this.loginField(log, 'method');
    return method ? AUDIT_LOG_LOGIN_METHODS[method] || method : null;
  }

  /** Quem tentou: o snapshot do usuário ou, sem conta, o e-mail digitado. */
  loginWho(log: AuditLog): string {
    return log.user_name || log.user_email || this.loginField(log, 'email') || 'Desconhecido';
  }

  sourceLabel(source: string): string {
    return this.sources.find((s) => s.value === source)?.label || source;
  }

  tableLabel(table: string): string {
    return this.tables.find((t) => t.value === table)?.label || table;
  }

  fieldsPreview(log: AuditLog): string {
    const fields = (log.changed_fields || []).map((c) => c.field);
    const shown = fields.slice(0, AuditLogsComponent.FIELDS_PREVIEW).join(', ');
    const rest = fields.length - AuditLogsComponent.FIELDS_PREVIEW;
    return rest > 0 ? `${shown} +${rest}` : shown;
  }

  /**
   * O backend marca `masked: true`; old/new nulos numa edição também indicam
   * valor escondido (o observer só grava em `updated` coluna que mudou).
   */
  isMasked(log: AuditLog, change: AuditLogChange): boolean {
    return !!change.masked || (log.action === 'updated' && change.old == null && change.new == null);
  }

  isStructured(value: any): boolean {
    return value !== null && typeof value === 'object';
  }

  formatValue(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    // Datas chegam em ISO-8601 (AuditObserver::normalize): mostra no fuso local.
    if (typeof value === 'string' && AuditLogsComponent.ISO_DATETIME.test(value)) {
      return formatDate(value, 'dd/MM/yyyy, HH:mm:ss', this.locale);
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  }

  copy(text: string): void {
    navigator.clipboard?.writeText(text).then(
      () => this.toastService.success('Copiado.', 2000),
      () => undefined
    );
  }

  trackById(_index: number, log: AuditLog): string {
    return log.id;
  }

  private navigate(filter: AuditLogFilter, page: number): void {
    const queryParams: Params = { page };
    FILTER_KEYS.forEach((key) => (queryParams[key] = filter[key] || null));

    // Mesma URL não emite queryParams de novo: o Filtrar sem mudança (ou a
    // mesma página) tem que recarregar mesmo assim — é o "atualizar" da tela.
    const same = page === this.page && FILTER_KEYS.every((key) => (filter[key] || null) === (this.applied[key] || null));
    if (same) {
      this.syncDraft();
      this.load();
      return;
    }

    this.router.navigate([ROUTE], { queryParams });
  }

  private readParams(params: Params): void {
    this.page = +params.page || 1;
    this.applied = {};
    FILTER_KEYS.forEach((key) => (this.applied[key] = params[key] || null));
    this.syncDraft();
  }

  /** O formulário volta a mostrar o filtro aplicado (URL, voltar do navegador). */
  private syncDraft(): void {
    this.filter = { ...this.applied };

    // Vindo da URL só temos o id; mantém o rótulo se for o mesmo usuário já escolhido.
    this.syncUserControl(this.ownerControl, this.ownerLookup, this.filter.owner_user_id);
    this.syncUserControl(this.operatorControl, this.operatorLookup, this.filter.user_id);
  }

  /** Mesmo fluxo da busca de usuário de AddCreditsComponent. */
  private filterUsers(
    input: ElementRef<HTMLInputElement>,
    control: FormControl<User | null>,
    lookup: UserLookup,
    setOptions: (users: Observable<User[]> | null) => void
  ): void {
    // Timer do próprio campo: digitar no outro não cancela esta busca.
    clearTimeout(lookup.timer);

    const filterValue = input?.nativeElement.value?.trim().toLowerCase() || '';

    // Apagou parte do usuário já escolhido: a seleção deixa de valer.
    const user = this.selectedUser(control);
    if (user && filterValue.length < this.displayUser(user).length) {
      control.patchValue(null);
      setOptions(null);
      return;
    }

    if (filterValue.length >= 3) {
      lookup.timer = setTimeout(() => setOptions(this.userService.filter(filterValue)), 200);
    }
  }

  private selectedUser(control: FormControl<User | null>): User | null {
    const value = control.value as User | string | null;
    return value && typeof value === 'object' && value.id ? value : null;
  }

  /**
   * Vindo da URL (ou de um atalho da linha) só temos o id: carrega o usuário
   * para o campo mostrar "Nome (e-mail)" em vez do uuid.
   */
  private syncUserControl(
    control: FormControl<User | null>,
    lookup: UserLookup,
    id: string | null | undefined
  ): void {
    const current = this.selectedUser(control);

    // Uma busca por campo: a de um id anterior não sobrescreve a atual.
    lookup.sub?.unsubscribe();
    lookup.sub = null;

    if (!id) {
      control.setValue(null, { emitEvent: false });
      return;
    }

    if (current?.id === id && current.name && current.email) {
      return;
    }

    if (current?.id !== id) {
      control.setValue({ id } as User, { emitEvent: false });
    }

    lookup.sub = this.userService.findById(id).subscribe({
      next: (user) => {
        if (user && this.selectedUser(control)?.id === id) {
          control.setValue(user, { emitEvent: false });
        }
      },
      error: () => undefined,
    });
  }

  /** 422 traz a mensagem do FormRequest (ex.: "to" anterior a "from"). */
  private errorMessage(err: HttpErrorResponse, fallback: string): string {
    if (err?.status === 422 && err.error?.errors) {
      const first = Object.values(err.error.errors as { [k: string]: string[] })[0];
      if (first?.length) {
        return first[0];
      }
    }
    return err?.error?.message && err.status === 422 ? err.error.message : fallback;
  }
}
